import { Button, Input } from '@bs/ui-components'
import { CheckCircle2, LoaderCircle } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { AuthPageShell, FormError } from './auth-page-shell'
import { requestTalentPasswordReset } from '@/features/auth/api'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      await requestTalentPasswordReset(email)
      setIsSent(true)
    } catch {
      setError('We could not send the reset email. Check the address and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell>
      <form onSubmit={submit} className="w-full max-w-[500px] text-[#404040]">
        <h1 className="text-center text-[30px] font-medium">Forgot Password</h1>

        {isSent ? (
          <div className="mt-8 text-center">
            <CheckCircle2 className="mx-auto size-14 text-accent" />
            <p className="mt-6 leading-7">
              If an account exists for <strong>{email}</strong>, a reset link has been sent. Check
              your inbox and spam folder.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-4 text-center text-[#8a8a8a]">
              Enter your email and we’ll send you a link to reset your password.
            </p>
            {error ? <FormError message={error} className="mt-7" /> : null}
            <div className="mt-8">
              <label htmlFor="reset-email" className="mb-4 block">Email</label>
              <Input
                id="reset-email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-11 rounded-xl px-4"
                required
              />
            </div>
            <Button
              type="submit"
              variant="accent"
              size="lg"
              className="mt-8 h-[50px] w-full text-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoaderCircle className="size-5 animate-spin" /> : null}
              {isSubmitting ? 'Sending…' : 'Send reset link'}
            </Button>
          </>
        )}

        <p className="mt-8 text-center">
          <Link to="/login" className="text-primary hover:underline">Back to login</Link>
        </p>
      </form>
    </AuthPageShell>
  )
}
